// src/services/actividades.ts
/**
 * Service para operaciones de Actividades.
 */
import { api } from './api';
import type {
  Actividad,
  ActividadCreate,
  ActividadUpdate,
  ActividadFiltros,
  ActividadListItem,
  ActividadContadores,
  ActividadHistorialItem,
  TipoActividadRef,
  ModuloRef,
  PlantillaRecurrente,
  ReordenarItem,
  ReparentRequest,
  BulkActualizarRequest,
  BulkCompletarRequest,
  BulkEliminarRequest,
  BulkActionResponse,
  BloqueActividad,
  BloqueActividadCreate,
  BloqueActividadUpdate,
} from '@/types';

export const actividadesService = {
  // CRUD
  list: (params?: ActividadFiltros) => {
    const searchParams = new URLSearchParams();
    if (params?.estado) searchParams.set('estado', params.estado);
    if (params?.responsable_id) searchParams.set('responsable_id', params.responsable_id);
    if (params?.fecha_desde) searchParams.set('fecha_desde', params.fecha_desde);
    if (params?.fecha_hasta) searchParams.set('fecha_hasta', params.fecha_hasta);
    if (params?.ticket_id) searchParams.set('ticket_id', params.ticket_id);
    if (params?.cliente_id) searchParams.set('cliente_id', params.cliente_id);
    if (params?.area) searchParams.set('area', params.area);
    if (params?.prioridad) searchParams.set('prioridad', params.prioridad);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.sin_fecha) searchParams.set('sin_fecha', 'true');
    if (params?.es_backlog !== undefined) searchParams.set('es_backlog', String(params.es_backlog));
    if (params?.en_foco !== undefined) searchParams.set('en_foco', String(params.en_foco));
    if (params?.compromiso_semana) searchParams.set('compromiso_semana', params.compromiso_semana);
    if (params?.incluir_subtareas_en_bloques) searchParams.set('incluir_subtareas_en_bloques', 'true');

    const query = searchParams.toString();
    return api.get<ActividadListItem[]>(`/actividades${query ? `?${query}` : ''}`);
  },

  get: (id: string) => api.get<Actividad>(`/actividades/${id}`),

  create: (data: ActividadCreate) => api.post<Actividad>('/actividades', data),

  update: (id: string, data: ActividadUpdate) =>
    api.patch<Actividad>(`/actividades/${id}`, data),

  delete: (id: string) => api.delete<void>(`/actividades/${id}`),

  complete: (id: string, completarSubtareas?: boolean) =>
    api.post<Actividad>(`/actividades/${id}/completar`, {
      completar_subtareas: completarSubtareas,
    }),

  reabrir: (id: string) =>
    api.post<Actividad>(`/actividades/${id}/reabrir`),

  // Consultas especiales
  contadores: () => api.get<ActividadContadores>('/actividades/contadores'),

  misActividadesHoy: () => api.get<ActividadListItem[]>('/actividades/hoy'),

  proximasActividades: (dias: number = 7) =>
    api.get<ActividadListItem[]>(`/actividades/proximas?dias=${dias}`),

  // Historial
  getHistorial: (actividadId: string) =>
    api.get<ActividadHistorialItem[]>(`/actividades/${actividadId}/historial`),

  // Subtareas
  getSubtareas: (actividadId: string) =>
    api.get<ActividadListItem[]>(`/actividades/${actividadId}/subtareas`),

  createSubtarea: (actividadId: string, data: ActividadCreate) =>
    api.post<Actividad>(`/actividades/${actividadId}/subtareas`, data),

  createSubtareasBatch: (actividadId: string, titulos: string[]) =>
    api.post<{ creadas: number; subtareas: ActividadListItem[] }>(
      `/actividades/${actividadId}/subtareas/batch`,
      { titulos }
    ),

  // Recurrencias
  getPlantillas: () => api.get<PlantillaRecurrente[]>('/actividades/recurrentes'),

  pausarPlantilla: (id: string) =>
    api.post<Actividad>(`/actividades/recurrentes/${id}/pausar`),

  reactivarPlantilla: (id: string) =>
    api.post<Actividad>(`/actividades/recurrentes/${id}/reactivar`),

  // Catalogos
  getTipos: () => api.get<TipoActividadRef[]>('/actividades/catalogos/tipos'),
  getModulos: () => api.get<ModuloRef[]>('/actividades/catalogos/modulos'),

  // Importacion masiva
  resolverRefs: (data: ResolverRefsRequest) =>
    api.post<ResolverRefsResponse>('/actividades/resolver-refs', data),

  bulkImport: (data: BulkImportRequest) =>
    api.post<BulkImportResponse>('/actividades/bulk', data),

  // Drag & drop
  reordenar: (items: ReordenarItem[]) =>
    api.patch<{ actualizados: number }>('/actividades/reordenar', { items }),

  reparent: (id: string, data: ReparentRequest) =>
    api.patch<ActividadListItem>(`/actividades/${id}/reparent`, data),

  // Acciones masivas (bulk actions)
  bulkUpdate: (data: BulkActualizarRequest) =>
    api.post<BulkActionResponse>('/actividades/bulk-update', data),

  bulkComplete: (data: BulkCompletarRequest) =>
    api.post<BulkActionResponse>('/actividades/bulk-complete', data),

  bulkDelete: (data: BulkEliminarRequest) =>
    api.post<BulkActionResponse>('/actividades/bulk-delete', data),

  // Bloques (secciones del día)
  listBloques: (fecha: string) =>
    api.get<BloqueActividad[]>(`/actividades/bloques?fecha=${fecha}`),

  createBloque: (data: BloqueActividadCreate) =>
    api.post<BloqueActividad>('/actividades/bloques', data),

  updateBloque: (id: string, data: BloqueActividadUpdate) =>
    api.patch<BloqueActividad>(`/actividades/bloques/${id}`, data),

  deleteBloque: (id: string) =>
    api.delete<void>(`/actividades/bloques/${id}`),

  reordenarBloques: (items: { id: string; orden: number }[]) =>
    api.patch<{ actualizados: number }>('/actividades/bloques/reordenar', { items }),

  // Limpieza diaria
  limpiezaDiaria: () =>
    api.post<{ ok: boolean }>('/actividades/limpieza-diaria'),
};

// --- Bulk Import Types ---

export interface ResolverRefsRequest {
  cliente_ref?: string;
  contacto_ref?: string;
  tipo_codigo?: string;
  proyecto_ref?: string;
  ubicacion_ref?: string;
  etiqueta_refs?: string[];
}

export interface ResolverRefsResponse {
  cliente_id?: string;
  cliente_nombre?: string;
  contacto_id?: string;
  contacto_nombre?: string;
  tipo_actividad_id?: string;
  tipo_actividad_nombre?: string;
  proyecto_id?: string;
  proyecto_nombre?: string;
  ubicacion_id?: string;
  ubicacion_nombre?: string;
  etiqueta_ids: string[];
  etiquetas_nuevas: string[];
  warnings: string[];
}

export interface BulkImportChildItem {
  linea: number;
  texto_original: string;
  titulo: string;
  cliente_ref?: string;
  contacto_ref?: string;
  tipo_actividad_codigo?: string;
  fecha_agendada?: string;
  hora_agendada?: string;
  fecha_limite?: string;
  hora_limite?: string;
  proyecto_ref?: string;
  ubicacion_ref?: string;
  etiqueta_refs?: string[];
  prioridad?: string;
  area?: string;
}

export interface BulkImportItem {
  linea: number;
  texto_original: string;
  titulo: string;
  cliente_ref?: string;
  contacto_ref?: string;
  tipo_actividad_codigo?: string;
  fecha_agendada?: string;
  hora_agendada?: string;
  fecha_limite?: string;
  hora_limite?: string;
  proyecto_ref?: string;
  ubicacion_ref?: string;
  etiqueta_refs?: string[];
  prioridad?: string;
  area?: string;
  children?: BulkImportChildItem[];
}

export interface BulkImportDefaults {
  area?: string;
  prioridad?: string;
  cliente_id?: string;
}

export interface BulkImportRequest {
  mode: 'preview' | 'commit';
  defaults?: BulkImportDefaults;
  items: BulkImportItem[];
}

export interface BulkItemPreview {
  titulo: string;
  cliente_nombre?: string;
  contacto_nombre?: string;
  tipo_actividad?: string;
  fecha_agendada?: string;
  hora_agendada?: string;
  fecha_limite?: string;
  prioridad: string;
  area: string;
  proyecto_nombre?: string;
  ubicacion_nombre?: string;
  etiquetas: string[];
  etiquetas_nuevas: string[];
}

export interface BulkItemResult {
  linea: number;
  texto_original: string;
  status: 'ok' | 'error' | 'warning';
  warnings: string[];
  errores: string[];
  actividad_preview?: BulkItemPreview;
  actividad?: { id: string; titulo: string };
  children?: BulkItemResult[];
}

export interface BulkImportResponse {
  mode: 'preview' | 'commit';
  resultados: BulkItemResult[];
  resumen: {
    total: number;
    ok: number;
    warnings: number;
    errores: number;
  };
}
